/**
 * Offline photo queue — Appendix C.2 Phase 4 deliverable 19 (P4-4).
 *
 * The premise of this module is that the jobsite has no signal. A slab pour, a
 * levee reach, a hospital basement, a parish in the week after a storm: the
 * crew photographs the work and the upload fails. If the failure is the user's
 * problem, the photo is lost — nobody walks back to a pour to re-shoot it, and
 * what was lost is the evidence a reimbursement is later argued from.
 *
 * So the queue is the *only* path to the server. The capture UI never uploads
 * directly, even when the device is online; it enqueues, and the queue drains.
 * That means the online and offline paths are the same path, and the offline
 * one cannot rot from never being exercised.
 *
 * The bytes live in IndexedDB rather than in memory because the crew closes the
 * tab, the phone kills the tab to reclaim memory, or the battery dies. A queue
 * that does not survive that is not a queue.
 */

const DB_NAME = 'calm-ops-photos';
const DB_VERSION = 1;
const STORE = 'pending';

/**
 * Attempts after which the item stops being retried automatically and is
 * surfaced to the user as needing attention.
 *
 * JUDGMENT, flagged: an item is never discarded by the queue itself, however
 * many times it fails. Six attempts only stops the *automatic* retry, so a
 * permanently rejected photo cannot spin against the network forever; the row
 * stays in IndexedDB with its error visible and a manual retry available. The
 * alternative — dropping after N failures — silently destroys the evidence
 * this module exists to protect.
 */
export const MAX_AUTOMATIC_ATTEMPTS = 6;

export interface QueuedPhotoInput {
  file: Blob;
  filename: string;
  projectId: string;
  category: string;
  title?: string;
  description?: string;
  tags?: string[];
}

export interface QueuedPhoto extends QueuedPhotoInput {
  id: string;
  /** Wall-clock time the crew pressed the button, not the time it uploaded. */
  queuedAt: string;
  /**
   * Tie-breaker for items enqueued within the same millisecond, which is the
   * normal case when a crew selects eight photos at once. `queuedAt` alone
   * leaves their order to whatever IndexedDB returns, and the order matters:
   * it is the order the work happened in, and it is the order the first photo
   * lands in when a weak connection only survives one upload.
   */
  seq: number;
  attempts: number;
  lastError: string | null;
  /** Set once the server has accepted it; the row is deleted immediately after. */
  status: 'pending' | 'uploading' | 'failed';
  /**
   * When a drain took ownership of this item, as epoch milliseconds.
   *
   * Two drains can be alive at once — the screen re-mounts, or a reconnect
   * fires while a drain from page load is still working. Without a claim both
   * would read the same pending row and upload it, and since the server keeps
   * no record of what it has already seen, that is two files in Dropbox and
   * two rows against the project for one photograph. The claim is written to
   * the store rather than held in memory so it survives the re-mount that
   * causes the problem.
   */
  claimedAt: number | null;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('queuedAt', 'queuedAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexeddb_open_failed'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('indexeddb_request_failed'));
        transaction.oncomplete = () => db.close();
      }),
  );
}

/**
 * Take ownership of one queued photo, atomically.
 *
 * Read-then-write across two transactions is not a claim: two drains both read
 * an unclaimed row, both write their own claim, and both upload. The read, the
 * check and the write all happen inside a single readwrite transaction here,
 * which IndexedDB serialises against the store — so exactly one caller can be
 * told it got the photo.
 *
 * Returns the claimed item, or null when somebody else holds it.
 */
function claimItem(id: string, staleBefore: number): Promise<QueuedPhoto | null> {
  return openDb().then(
    (db) =>
      new Promise<QueuedPhoto | null>((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const store = transaction.objectStore(STORE);
        const read = store.get(id);
        let claimed: QueuedPhoto | null = null;

        read.onsuccess = () => {
          const item = read.result as QueuedPhoto | undefined;
          if (!item) return;
          const heldByAnother = item.claimedAt !== null && item.claimedAt > staleBefore;
          if (heldByAnother || item.attempts >= MAX_AUTOMATIC_ATTEMPTS) return;
          claimed = { ...item, status: 'uploading', claimedAt: Date.now() };
          store.put(claimed);
        };
        read.onerror = () => reject(read.error ?? new Error('indexeddb_request_failed'));
        transaction.oncomplete = () => {
          db.close();
          resolve(claimed);
        };
        transaction.onerror = () =>
          reject(transaction.error ?? new Error('indexeddb_request_failed'));
      }),
  );
}

/** Whether this browser can hold a queue at all. */
export function queueSupported(): boolean {
  return typeof indexedDB !== 'undefined';
}

/**
 * Monotonic within a session, and reset on reload. A reload takes far longer
 * than the millisecond `queuedAt` resolves to, so a restarted counter cannot
 * collide with an item already in the store.
 */
let sequence = 0;

function newId(): string {
  const c = globalThis.crypto;
  if (c && 'randomUUID' in c) return c.randomUUID();
  return `q-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function enqueue(input: QueuedPhotoInput): Promise<QueuedPhoto> {
  const item: QueuedPhoto = {
    ...input,
    id: newId(),
    queuedAt: new Date().toISOString(),
    seq: (sequence += 1),
    claimedAt: null,
    attempts: 0,
    lastError: null,
    status: 'pending',
  };
  await tx('readwrite', (store) => store.put(item));
  return item;
}

/** Everything still waiting, oldest first — the order the work happened in. */
export async function listQueued(): Promise<QueuedPhoto[]> {
  const all = await tx<QueuedPhoto[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedPhoto[]>);
  return all.sort(
    (a, b) => a.queuedAt.localeCompare(b.queuedAt) || (a.seq ?? 0) - (b.seq ?? 0),
  );
}

export async function removeQueued(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id) as unknown as IDBRequest<undefined>);
}

async function updateQueued(item: QueuedPhoto): Promise<void> {
  await tx('readwrite', (store) => store.put(item));
}

/** Clears a failed item's attempt count so the next drain picks it up again. */
export async function retryQueued(id: string): Promise<void> {
  const all = await listQueued();
  const item = all.find((entry) => entry.id === id);
  if (!item) return;
  await updateQueued({ ...item, attempts: 0, status: 'pending', lastError: null, claimedAt: null });
}

/**
 * How long a claim is honoured before another drain may take the item.
 *
 * It has to outlast a genuine slow upload — a 12 MP photo over one bar can
 * take minutes — because reclaiming an item that is still uploading recreates
 * the duplicate the claim exists to prevent. It also has to expire, because a
 * tab closed mid-upload leaves a claim nobody will ever release, and a photo
 * frozen forever is the other failure this module refuses.
 */
export const CLAIM_TIMEOUT_MS = 10 * 60 * 1000;

export interface DrainResult {
  uploaded: number;
  failed: number;
  remaining: number;
}

/**
 * Upload everything the queue holds, in order.
 *
 * Sequential rather than parallel: a crew coming back into coverage has a weak
 * connection, and eight simultaneous multi-megabyte uploads on a weak link is
 * how all eight time out. One at a time means the first photo lands even if
 * the signal dies during the second.
 *
 * A 4xx other than 408/429 is treated as permanent — the server has judged the
 * request, and repeating it verbatim will not change the answer. Anything else
 * is treated as transient and left to the next drain.
 */
export async function drainQueue(
  upload: (item: QueuedPhoto) => Promise<void>,
  options: { onProgress?: (item: QueuedPhoto) => void } = {},
): Promise<DrainResult> {
  let uploaded = 0;
  let failed = 0;

  const staleBefore = Date.now() - CLAIM_TIMEOUT_MS;

  for (const listed of await listQueued()) {
    if (listed.attempts >= MAX_AUTOMATIC_ATTEMPTS) {
      failed += 1;
      continue;
    }
    // Whoever wins the claim uploads it; everyone else moves on.
    const item = await claimItem(listed.id, staleBefore);
    if (!item) continue;

    options.onProgress?.(item);
    try {
      await upload(item);
      await removeQueued(item.id);
      uploaded += 1;
    } catch (err) {
      const attempts = item.attempts + 1;
      const permanent = isPermanent(err);
      await updateQueued({
        ...item,
        attempts: permanent ? MAX_AUTOMATIC_ATTEMPTS : attempts,
        status: 'failed',
        claimedAt: null,
        lastError: err instanceof Error ? err.message : 'upload_failed',
      });
      failed += 1;
      // A transient failure almost certainly means the link is gone; stop the
      // drain rather than burning attempt counts on every remaining item.
      if (!permanent) break;
    }
  }

  return { uploaded, failed, remaining: (await listQueued()).length };
}

function isPermanent(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  if (typeof status !== 'number') return false;
  if (status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}
